import {
  doc,
  getDoc,
  onSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { getFirebaseServices } from "./firebase";

export function syncMetadataReference(uid: string) {
  return doc(getFirebaseServices().firestore, "users", uid, "sync", "meta");
}

function assertCurrentUser(uid: string): void {
  if (!uid || getFirebaseServices().auth.currentUser?.uid !== uid) {
    throw new Error("The requested workspace does not belong to the signed-in account.");
  }
}

function changeMarkerFingerprint(value: unknown): string {
  if (value === null || value === undefined) return "missing";
  if (typeof value === "string") return `string:${value}`;
  if (value instanceof Date) return `date:${value.toISOString()}`;
  if (typeof value === "object") {
    const raw = value as Record<string, unknown>;
    const seconds = raw.seconds ?? raw._seconds;
    const nanoseconds = raw.nanoseconds ?? raw._nanoseconds;
    if (typeof seconds === "number" && typeof nanoseconds === "number") {
      return `timestamp:${seconds}:${nanoseconds}`;
    }
    const toMillis = raw.toMillis;
    if (typeof toMillis === "function") {
      const milliseconds = (toMillis as () => number).call(value);
      if (Number.isFinite(milliseconds)) return `millis:${milliseconds}`;
    }
  }
  return `unknown:${String(value)}`;
}

export interface SyncChangeMarker {
  /** Opaque comparison token for `lastServerChangeAt`. */
  fingerprint: string;
}

function markerFromData(value: unknown): SyncChangeMarker {
  const raw = value && typeof value === "object"
    ? value as Record<string, unknown>
    : {};
  return { fingerprint: changeMarkerFingerprint(raw.lastServerChangeAt) };
}

/** Capture the case-change marker immediately before a full collection read. */
export async function readSyncChangeMarker(uid: string): Promise<SyncChangeMarker> {
  assertCurrentUser(uid);
  const snapshot = await getDoc(syncMetadataReference(uid));
  return markerFromData(snapshot.exists() ? snapshot.data() : {});
}

export type CaseCountReconciliationResult =
  | "already-correct"
  | "marker-changed";

/**
 * In Sync v3 the mutation service owns all metadata and counts. The marker is
 * still read before a full fetch to close its listener race, but Studio must
 * never write `sync/meta` directly.
 */
export async function reconcileSyncCaseCount(
  uid: string,
  expectedMarker: SyncChangeMarker,
  activeCaseCount: number,
): Promise<CaseCountReconciliationResult> {
  assertCurrentUser(uid);
  if (!Number.isSafeInteger(activeCaseCount) || activeCaseCount < 0) {
    throw new Error("The active case count must be a non-negative safe integer.");
  }

  void activeCaseCount;
  const snapshot = await getDoc(syncMetadataReference(uid));
  const currentMarker = markerFromData(snapshot.exists() ? snapshot.data() : {});
  return currentMarker.fingerprint === expectedMarker.fingerprint ? "already-correct" : "marker-changed";
}

/** Observe marker changes made by other clients, ignoring the initial value. */
export function observeSyncMetadataChanges(
  uid: string,
  observer: () => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  let initial = true;
  let previousMarker = "";
  return onSnapshot(syncMetadataReference(uid), (snapshot) => {
    const marker = markerFromData(snapshot.exists() ? snapshot.data() : {}).fingerprint;
    if (initial) {
      initial = false;
      previousMarker = marker;
      return;
    }
    if (snapshot.metadata.hasPendingWrites) return;
    if (marker === previousMarker) return;
    previousMarker = marker;
    observer();
  }, (reason) => onError?.(reason instanceof Error ? reason : new Error(String(reason))));
}
